import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Ruler } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { to: "/", label: "Pagrindinis" },
  { to: "/paslaugos", label: "Paslaugos" },
  { to: "/galerija", label: "Galerija" },
  { to: "/kontaktai", label: "Kontaktai" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "border-slate-200/80 bg-white/85" : "border-slate-200/50 bg-white/80"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-12">
        <Link to="/" data-testid="nav-home-link" aria-label="ART Ceiling pradžia">
          <Logo />
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-sky-600 ${
                  isActive ? "text-sky-600" : "text-slate-600"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/#matavimas"
            data-testid="nav-measurement-button"
            className="group inline-flex items-center gap-2 rounded-full bg-navy-900 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-[0_10px_30px_-10px_rgba(10,25,47,0.5)]"
          >
            <Ruler className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            Nemokamas matavimas
          </Link>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="rounded-lg p-2 text-navy-900 lg:hidden"
          aria-label="Meniu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-200/80 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="rounded-xl px-4 py-3 text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/#matavimas"
                data-testid="nav-mobile-measurement-button"
                className="mt-2 rounded-xl bg-navy-900 px-4 py-3 text-center text-base font-semibold text-white"
              >
                Nemokamas matavimas
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
